/**
 * CopilotWidget — floating companion rendered in a portal on document.body.
 *
 *  - Minimized: round floating bubble (bottom-right) with pending badge.
 *  - Open: 380x560 window, draggable by its title bar (hand-rolled pointer
 *    events, no lib), messages, proposed-action cards (Confirmer/Rejeter),
 *    collapsible execution timeline, input (Enter to send) and a
 *    backend-availability indicator. Dark slate styling, works on any page.
 */

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { getBalance } from '@/lib/api/credits';
import { ModelPicker } from '@/features/credits';
import { useCopilotStore } from '@/stores/copilot-store';
import type { PlayerPace, PlayerStatus, PlayerStep, PlayerStepStatus } from '@/stores/copilot-store';
import { useAdvancedMode } from '@/components/AdvancedMode';

import { getCurrent } from './context-registry';
import { uploadCopilotFile } from './file-upload';
import { RichResponse, type RichAction } from './rich/RichResponse';
import { VoiceRecorder } from './VoiceRecorder';
import type { CopilotAction, CopilotActionType, CopilotMessage, ExecutionStatus } from './types';

const ACTION_LABELS: Record<CopilotActionType, string> = {
  navigate: 'Navigation',
  open_form: 'Ouvrir un formulaire',
  fill_field: 'Remplir un champ',
  fill_form: 'Remplir un formulaire',
  submit_form: 'Soumettre un formulaire',
  select_table_rows: 'Sélectionner des lignes',
  highlight: 'Mettre en évidence',
  open_modal: 'Ouvrir une fenêtre',
  scroll_to: 'Défiler vers',
  toast: 'Notification',
  import_records: 'Importer des enregistrements',
  wait_user_confirm: 'Attendre une confirmation',
  execute_tool: 'Exécuter une action',
};

const STATUS_STYLES: Record<ExecutionStatus, { dot: string; label: string }> = {
  executed: { dot: 'bg-emerald-400', label: 'exécutée' },
  failed: { dot: 'bg-rose-400', label: 'échouée' },
  skipped: { dot: 'bg-slate-400', label: 'ignorée' },
};

// Paced player — labels/styles for the opt-in step-by-step "watch & intervene"
// mode (see copilot-store.ts `runPlayerLoop`). Purely presentational; the
// player's state machine lives entirely in the store.
const PLAYER_STATUS_LABELS: Record<PlayerStatus, string> = {
  idle: 'Prêt',
  playing: 'Lecture en cours…',
  paused: 'En pause',
  waiting: 'En attente de votre confirmation',
  done: 'Terminé',
};

const PLAYER_STEP_STYLES: Record<PlayerStepStatus, { dot: string; label: string }> = {
  pending: { dot: 'bg-slate-500', label: 'en attente' },
  running: { dot: 'bg-indigo-400 animate-pulse', label: 'en cours' },
  waiting: { dot: 'bg-amber-400', label: 'pause : confirmation' },
  executed: STATUS_STYLES.executed,
  failed: STATUS_STYLES.failed,
  skipped: STATUS_STYLES.skipped,
};

function clampPos(p: { x: number; y: number }): { x: number; y: number } {
  if (typeof window === 'undefined') return p;
  return {
    x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - 100)),
    y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - 56)),
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
}

function summarizeTarget(action: CopilotAction): string {
  const t = action.target;
  if (!t) return '';
  const parts: string[] = [];
  if (t.tool) parts.push(`outil : ${t.tool}`);
  if (t.route) parts.push(`route : ${t.route}`);
  if (t.page_id) parts.push(`page : ${t.page_id}`);
  if (t.form_id) parts.push(`formulaire : ${t.form_id}`);
  if (t.field) parts.push(`champ : ${t.field}`);
  return parts.join(' · ');
}

/**
 * Turn a RichResponse form submit into a structured, machine-actionable user
 * turn. There is no direct "submit these form values" copilot endpoint — the
 * confirm gate (`/actions/{id}/confirm`) + `_SUBMIT_FORM_BRIDGE` / tool call
 * only fire on an action the backend PROPOSED during a message turn. So a
 * `form` block's submit is fed back as a new turn carrying the `formId`
 * (its `_SUBMIT_FORM_BRIDGE` key), the backend `tool`, and the entered values;
 * the backend then proposes the real `submit_form`/`execute_tool` action, which
 * flows through the exact same normalize → pending/agent-autorun →
 * `runRegisteredAction` → CASL/HIL-gated persistence path as every other
 * Action Protocol submit. Nothing here bypasses that gate.
 */
function describeRichSubmit(action: RichAction): string {
  const entries = Object.entries(action.values ?? {});
  const rendered =
    entries.length > 0
      ? entries
          .map(([key, value]) => {
            const shown = typeof value === 'boolean' ? (value ? 'oui' : 'non') : String(value);
            return `- ${key} : ${shown}`;
          })
          .join('\n')
      : '(aucune valeur saisie)';
  const head = action.formId
    ? `Soumettre le formulaire « ${action.formId} »`
    : action.tool
      ? `Exécuter l’action « ${action.tool} »`
      : 'Soumettre le formulaire';
  const toolNote = action.tool && action.formId ? ` (outil : ${action.tool})` : '';
  return `${head}${toolNote} avec les valeurs suivantes :\n${rendered}`;
}

function MessageBubble({
  message,
  onRichAction,
}: {
  message: CopilotMessage;
  onRichAction: (action: RichAction) => void;
}) {
  if (message.role === 'system') {
    return <p className="text-center text-[11px] italic text-slate-500">{message.content}</p>;
  }
  const isUser = message.role === 'user';
  const hasView = !isUser && Array.isArray(message.view) && message.view.length > 0;
  const hasBubble = message.content !== '' || (message.actions && message.actions.length > 0);
  return (
    <div className={`flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
      {hasBubble && (
        <div
          className={`max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-2.5 py-1.5 text-sm ${
            isUser ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-100'
          }`}
        >
          {message.content}
          {message.actions && message.actions.length > 0 && (
            <p className={`mt-1 text-[10px] ${isUser ? 'text-indigo-200' : 'text-slate-400'}`}>
              {message.actions.length} action(s) proposée(s)
            </p>
          )}
        </div>
      )}
      {hasView && (
        <div className="w-full max-w-full">
          <RichResponse view={message.view} onAction={onRichAction} />
        </div>
      )}
    </div>
  );
}

function ActionCard({
  action,
  onConfirm,
  onReject,
}: {
  action: CopilotAction;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const label = ACTION_LABELS[action.type] ?? action.type;
  const target = summarizeTarget(action);
  return (
    <div className="rounded-lg border border-slate-600 bg-slate-800 p-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-100">{label}</span>
        {action.preview && (
          <span className="rounded border border-amber-500/50 px-1 text-[10px] text-amber-300">
            aperçu
          </span>
        )}
      </div>
      {target && <p className="mt-0.5 truncate text-[11px] text-slate-400">{target}</p>}
      <div className="mt-1.5 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          aria-label={`Confirmer l’action : ${label}`}
          className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-500"
        >
          {action.requires_confirmation ? 'Confirmer' : 'Exécuter'}
        </button>
        <button
          type="button"
          onClick={onReject}
          aria-label={`Rejeter l’action : ${label}`}
          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
        >
          Rejeter
        </button>
      </div>
    </div>
  );
}

/** One row of the paced player's step queue — highlights the current step. */
function PlayerStepRow({
  step,
  index,
  current,
}: {
  step: PlayerStep;
  index: number;
  current: boolean;
}) {
  const style = PLAYER_STEP_STYLES[step.status];
  const label = ACTION_LABELS[step.action.type] ?? step.action.type;
  const target = summarizeTarget(step.action);
  return (
    <li
      aria-current={current ? 'step' : undefined}
      className={`flex items-start gap-2 rounded px-1.5 py-1 ${
        current ? 'bg-indigo-500/15 ring-1 ring-inset ring-indigo-400' : ''
      }`}
    >
      <span
        className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
        title={style.label}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-slate-200">
          {index + 1}. {label}
        </span>
        {target && <span className="block truncate text-[10px] text-slate-500">{target}</span>}
        {step.detail && (
          <span className="block truncate text-[10px] text-slate-500">{step.detail}</span>
        )}
      </span>
    </li>
  );
}

export function CopilotWidget() {
  const {
    open,
    minimized,
    position,
    mode,
    messages,
    pendingActions,
    timeline,
    toasts,
    backendAvailable,
    sending,
    model,
    temperature,
    maxSteps,
    playerRunMode,
    playerPace,
    playerStatus,
    playerSteps,
    playerStepIndex,
    playerWaitPrompt,
    openWidget,
    setMinimized,
    setPosition,
    setMode,
    setModel,
    setTemperature,
    setMaxSteps,
    sendMessage,
    confirmAction,
    rejectAction,
    pushToast,
    dismissToast,
    setPlayerRunMode,
    setPlayerPace,
    playerPlay,
    playerPause,
    playerStepOnce,
    playerStop,
    playerContinueWait,
    playerIntervene,
  } = useCopilotStore();

  const { advanced: copilotAdvanced, toggle: toggleCopilotAdvanced } = useAdvancedMode('copilot');

  const [draft, setDraft] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [livePos, setLivePos] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const lastDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composeInputRef = useRef<HTMLInputElement>(null);

  const pos = clampPos(livePos ?? position);
  const canAttach = backendAvailable && !sending;

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending]);

  // Credit-balance transparency: fetch the tenant's wallet whenever the
  // widget is opened, and refresh it right after a turn (which consumes
  // credits server-side). Best-effort only — a failure just hides the
  // indicator, it must never break the widget.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getBalance()
      .then((wallet) => {
        if (!cancelled) setCreditBalance(wallet.available);
      })
      .catch(() => {
        if (!cancelled) setCreditBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const refreshBalance = () =>
    void getBalance()
      .then((wallet) => setCreditBalance(wallet.available))
      .catch(() => {
        /* keep last known balance on refresh failure */
      });

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    void sendMessage(text, getCurrent()).then(refreshBalance);
  };

  // "Intervenir" — the paced-player affordance: pauses an active playing run
  // BEFORE sending (via `playerIntervene`) so the intervention and the
  // auto-advancing queue never race on screen; a run already 'waiting' on a
  // wait_user_confirm step stays 'waiting' (still resolved via Continuer).
  const handleIntervene = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    void playerIntervene(text, getCurrent()).then(refreshBalance);
  };

  // "Modifier" / "Poser une question" on a wait_user_confirm pause: both hand
  // control back to the compose input (Intervenir remains the explicit way to
  // send) rather than auto-advancing the run.
  const focusCompose = () => composeInputRef.current?.focus();

  // A rich `form` block submit (RichResponse onAction) is routed through the
  // real Action Protocol submit path: it becomes a structured user turn, the
  // backend proposes the matching submit_form/execute_tool action, and that runs
  // through the same confirm gate + `_SUBMIT_FORM_BRIDGE` / CASL screen as any
  // other copilot action. See `describeRichSubmit`.
  const handleRichAction = (action: RichAction) => {
    if (action.type !== 'submit_form' || sending) return;
    void sendMessage(describeRichSubmit(action), getCurrent()).then(refreshBalance);
  };

  const onFileChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (file) void uploadCopilotFile(file);
  };

  const onDragOver = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!canAttach) return;
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      setDragging(true);
    }
  };

  const onDragLeave = (e: ReactDragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>) => {
    setDragging(false);
    if (!canAttach) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      e.preventDefault();
      void uploadCopilotFile(file);
    }
  };

  const onTitlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Do not start a drag from the title-bar buttons.
    if ((e.target as HTMLElement).closest('button')) return;
    dragOffsetRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTitlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const offset = dragOffsetRef.current;
    if (!offset) return;
    const next = clampPos({ x: e.clientX - offset.dx, y: e.clientY - offset.dy });
    lastDragPosRef.current = next;
    setLivePos(next);
  };

  const onTitlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer capture already released
    }
    if (lastDragPosRef.current) {
      setPosition(lastDragPosRef.current);
      lastDragPosRef.current = null;
    }
    setLivePos(null);
  };

  const showWindow = open && !minimized;

  const bubble = (
    <button
      type="button"
      onClick={openWidget}
      aria-label="Ouvrir le copilot"
      className="fixed bottom-5 right-5 z-[10001] flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-xl transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 10h8m-8 4h5m-9 6 2.6-2.6A2 2 0 0 1 8 17h9a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v13Z"
        />
      </svg>
      {pendingActions.length > 0 && (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-slate-900"
          aria-label={`${pendingActions.length} action(s) en attente`}
        >
          {pendingActions.length}
        </span>
      )}
      {!backendAvailable && (
        <span
          className="absolute -left-0.5 -top-0.5 h-3 w-3 rounded-full border border-slate-900 bg-amber-400"
          title="Backend copilot indisponible"
          aria-hidden="true"
        />
      )}
    </button>
  );

  const panel = (
    <div
      role="dialog"
      aria-label="Copilot"
      className="fixed z-[10001] flex h-[560px] max-h-[calc(100vh-16px)] w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
      style={{ left: pos.x, top: pos.y }}
    >
      {/* Title bar — drag handle */}
      <div
        className="flex h-11 shrink-0 cursor-move touch-none select-none items-center gap-2 border-b border-slate-700 bg-slate-800 px-3"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={onTitlePointerUp}
        onPointerCancel={onTitlePointerUp}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            backendAvailable ? 'bg-emerald-400' : 'bg-amber-400'
          }`}
          title={backendAvailable ? 'Backend copilot connecté' : 'Backend copilot indisponible'}
          aria-hidden="true"
        />
        <span className="text-sm font-semibold">Copilot</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            mode === 'agent' ? 'bg-amber-500/20 text-amber-300' : 'bg-sky-500/20 text-sky-300'
          }`}
        >
          {mode === 'agent' ? 'Agent' : 'Assistant'}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            role="switch"
            aria-checked={copilotAdvanced}
            onClick={toggleCopilotAdvanced}
            aria-label="Mode avancé"
            title={copilotAdvanced ? 'Masquer les options avancées' : 'Afficher les options avancées'}
            className={`rounded px-2 py-1 text-[11px] ${
              copilotAdvanced ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Avancé
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={playerRunMode === 'paced'}
            onClick={() => setPlayerRunMode(playerRunMode === 'paced' ? 'auto' : 'paced')}
            aria-label={
              playerRunMode === 'paced'
                ? 'Désactiver le mode pas-à-pas'
                : 'Activer le mode pas-à-pas'
            }
            title="Exécution observable : lecture, pause, étape par étape et intervention"
            className={`rounded px-2 py-1 text-[11px] ${
              playerRunMode === 'paced' ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Pas-à-pas
          </button>
          <button
            type="button"
            onClick={() => setMode(mode === 'agent' ? 'assistant' : 'agent')}
            aria-label={
              mode === 'agent' ? 'Passer en mode assistant' : 'Passer en mode agent'
            }
            className="rounded px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-700"
          >
            {mode === 'agent' ? 'Mode assistant' : 'Mode agent'}
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label="Réduire le copilot"
            className="rounded px-2 py-1 text-slate-300 hover:bg-slate-700"
          >
            <span aria-hidden="true">—</span>
          </button>
        </div>
      </div>

      {/* Backend availability banner */}
      {!backendAvailable && (
        <div
          role="status"
          className="shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300"
        >
          Backend copilot indisponible — mode local, actions simulées marquées « aperçu ».
        </div>
      )}

      {/* Advanced options (model / temperature / agent steps) */}
      {copilotAdvanced && (
        <div className="shrink-0 space-y-2 border-b border-slate-700 bg-slate-800/60 px-3 py-2 text-xs text-slate-300">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Options avancées
          </p>
          <div className="flex items-center justify-between gap-2">
            <span>Modèle</span>
            <ModelPicker value={model} onChange={setModel} compact />
          </div>
          <label className="flex items-center justify-between gap-2">
            <span>Température ({temperature.toFixed(1)})</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              aria-label="Température"
              className="w-28 accent-indigo-500"
            />
          </label>
          {mode === 'agent' && (
            <label className="flex items-center justify-between gap-2">
              <span>Étapes max (agent)</span>
              <input
                type="number"
                min={1}
                max={10}
                value={maxSteps}
                onChange={(e) => setMaxSteps(Number(e.target.value))}
                aria-label="Nombre maximal d’étapes de l’agent"
                className="w-16 rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-xs text-slate-100"
              />
            </label>
          )}
          <p className="text-[10px] text-slate-500">
            Envoyées au copilot ; ignorées tant que le backend ne les exploite pas.
          </p>
        </div>
      )}

      {/* Messages */}
      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label="Messages du copilot"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative flex-1 space-y-2 overflow-y-auto px-3 py-2 ${
          dragging ? 'ring-2 ring-inset ring-indigo-500' : ''
        }`}
      >
        {messages.length === 0 && (
          <p className="pt-8 text-center text-xs text-slate-500">
            Posez une question, déposez un fichier ou demandez une action au copilot.
          </p>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} onRichAction={handleRichAction} />
        ))}
        {sending && <p className="text-xs text-slate-400">Le copilot réfléchit…</p>}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-slate-900/80 text-sm font-medium text-indigo-300">
            Déposez le fichier pour l’analyser
          </div>
        )}
      </div>

      {/* Paced player — opt-in step-by-step "watch & intervene" mode. Only
          meaningful in agent mode: the store only routes actions into the
          player queue when `mode === 'agent'` (assistant mode already puts
          every action on a manual Confirmer/Rejeter card, unchanged). */}
      {playerRunMode === 'paced' && mode === 'agent' && (
        <div className="max-h-56 shrink-0 space-y-2 overflow-y-auto border-t border-slate-700 bg-slate-800/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Lecteur pas-à-pas
            </p>
            <span role="status" className="text-[10px] text-slate-400">
              {PLAYER_STATUS_LABELS[playerStatus]}
              {playerSteps.length > 0
                ? ` (${Math.min(playerStepIndex + 1, playerSteps.length)}/${playerSteps.length})`
                : ''}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={playerStatus === 'playing' ? playerPause : playerPlay}
              disabled={
                playerStatus === 'waiting' ||
                (playerStatus !== 'playing' && playerStepIndex >= playerSteps.length)
              }
              aria-label={playerStatus === 'playing' ? 'Mettre en pause la lecture' : 'Lancer la lecture'}
              className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {playerStatus === 'playing' ? 'Pause' : 'Lecture'}
            </button>
            <button
              type="button"
              onClick={playerStepOnce}
              disabled={
                playerStatus === 'playing' || playerStatus === 'waiting' || playerStepIndex >= playerSteps.length
              }
              aria-label="Exécuter l’étape suivante"
              className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Étape suivante
            </button>
            <button
              type="button"
              onClick={playerStop}
              disabled={playerSteps.length === 0}
              aria-label="Arrêter la lecture pas-à-pas"
              className="rounded border border-rose-500/50 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Arrêter
            </button>
            <label className="ml-auto flex items-center gap-1 text-[11px] text-slate-400">
              <span className="sr-only">Vitesse de lecture</span>
              <select
                value={playerPace}
                onChange={(e) => setPlayerPace(e.target.value as PlayerPace)}
                aria-label="Vitesse de lecture du copilot"
                className="rounded border border-slate-600 bg-slate-900 px-1.5 py-1 text-[11px] text-slate-100"
              >
                <option value="lent">Lent</option>
                <option value="normal">Normal</option>
                <option value="rapide">Rapide</option>
              </select>
            </label>
          </div>

          {playerStatus === 'waiting' && playerWaitPrompt && (
            <div role="alert" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2">
              <p className="text-[11px] text-amber-200">{playerWaitPrompt}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={playerContinueWait}
                  aria-label="Continuer l’exécution"
                  className="rounded bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-500"
                >
                  Continuer
                </button>
                <button
                  type="button"
                  onClick={focusCompose}
                  aria-label="Modifier la suite via un message au copilot"
                  className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700"
                >
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={focusCompose}
                  aria-label="Poser une question au copilot"
                  className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700"
                >
                  Poser une question
                </button>
              </div>
            </div>
          )}

          {playerSteps.length > 0 && (
            <ul className="space-y-0.5" aria-label="File d’exécution pas-à-pas">
              {playerSteps.map((step, index) => (
                <PlayerStepRow
                  key={`${step.action.action_id}-${index}`}
                  step={step}
                  index={index}
                  current={index === playerStepIndex}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Proposed actions */}
      {pendingActions.length > 0 && (
        <div className="max-h-44 shrink-0 space-y-2 overflow-y-auto border-t border-slate-700 bg-slate-800/60 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Actions proposées
          </p>
          {pendingActions.map((action) => (
            <ActionCard
              key={action.action_id}
              action={action}
              onConfirm={() => confirmAction(action.action_id)}
              onReject={() => rejectAction(action.action_id)}
            />
          ))}
        </div>
      )}

      {/* Execution timeline (collapsible) */}
      <div className="shrink-0 border-t border-slate-700">
        <button
          type="button"
          onClick={() => setShowTimeline((v) => !v)}
          aria-expanded={showTimeline}
          aria-label="Afficher ou masquer la timeline des actions exécutées"
          className="flex w-full items-center justify-between px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-800"
        >
          <span>Timeline des actions ({timeline.length})</span>
          <span aria-hidden="true">{showTimeline ? '▾' : '▸'}</span>
        </button>
        {showTimeline && (
          <ul className="max-h-32 overflow-y-auto px-3 pb-2 text-xs" aria-label="Actions exécutées">
            {timeline.length === 0 && (
              <li className="text-slate-500">Aucune action exécutée pour l’instant.</li>
            )}
            {[...timeline].reverse().map((entry, index) => {
              const style = STATUS_STYLES[entry.status];
              return (
                <li key={`${entry.at}-${index}`} className="flex items-start gap-2 py-0.5">
                  <span
                    className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${style.dot}`}
                    title={style.label}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 break-words text-slate-300">
                    {ACTION_LABELS[entry.action.type] ?? entry.action.type}
                    {entry.detail ? ` — ${entry.detail}` : ''}
                  </span>
                  <span className="shrink-0 text-slate-500">{formatTime(entry.at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Credit balance — transparency on remaining AI credits */}
      {creditBalance !== null && (
        <div
          className="flex shrink-0 items-center justify-end border-t border-slate-700 bg-slate-800/40 px-3 py-1 text-[10px] text-slate-400"
          title="Solde de crédits IA restants pour ce compte"
        >
          {Math.round(creditBalance).toLocaleString('fr-FR')} crédit
          {Math.round(creditBalance) === 1 ? '' : 's'} restant
          {Math.round(creditBalance) === 1 ? '' : 's'}
        </div>
      )}

      {/* Input */}
      <div className="flex shrink-0 items-center gap-2 border-t border-slate-700 bg-slate-800 p-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          tabIndex={-1}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttach}
          aria-label="Joindre un fichier"
          title={
            backendAvailable
              ? 'Joindre un fichier à analyser'
              : 'Import de fichier indisponible — backend copilot hors ligne'
          }
          className="shrink-0 rounded-md border border-slate-600 p-1.5 text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
            />
          </svg>
        </button>
        <input
          ref={composeInputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={mode === 'agent' ? 'Demandez une action…' : 'Posez une question…'}
          aria-label="Message pour le copilot"
          className="min-w-0 flex-1 rounded-md border border-slate-600 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
        />
        <VoiceRecorder
          onTranscript={(text) => {
            const clean = text.trim();
            if (clean) setDraft((prev) => (prev.trim() ? `${prev.trim()} ${clean}` : clean));
          }}
          onError={(msg) => pushToast(msg)}
          language="fr"
          disabled={sending}
          className="shrink-0"
        />
        {playerRunMode === 'paced' && mode === 'agent' && (
          <button
            type="button"
            onClick={handleIntervene}
            disabled={sending || draft.trim() === ''}
            aria-label="Intervenir : mettre en pause la lecture pas-à-pas et envoyer ce message"
            title="Met en pause la lecture pas-à-pas puis envoie votre message"
            className="shrink-0 rounded-md border border-amber-500/60 px-2.5 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Intervenir
          </button>
        )}
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || draft.trim() === ''}
          aria-label="Envoyer le message"
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Envoyer
        </button>
      </div>
    </div>
  );

  const toastStack = toasts.length > 0 && (
    <div className="fixed bottom-24 right-5 z-[10002] flex w-72 flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 shadow-lg"
        >
          <span className="min-w-0 flex-1 break-words">{toast.message}</span>
          <button
            type="button"
            onClick={() => dismissToast(toast.id)}
            aria-label="Fermer la notification"
            className="shrink-0 text-slate-400 hover:text-slate-200"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );

  return createPortal(
    <>
      {showWindow ? panel : bubble}
      {toastStack}
    </>,
    document.body,
  );
}
